import type { Candidate } from '../candidate'
import type { DerivedFacts } from '../facts'
import { RULE_IDS } from '../version'
import { evidenceFor, missingEvidenceFor, watchItemsFor } from './shared'

/**
 * Maintenance (phase-system.md, rules-engine.md).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  STABLE WEIGHT IS SUCCESS HERE, NOT A PLATEAU.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * This is the phase most likely to be got wrong by applying fat-loss logic to
 * it, and rules-engine.md says so explicitly: "never demand ongoing weight loss
 * merely because fat-loss logic would". The same −0.4%/week that earns "stay the
 * course" during fat loss is *off target* here — it means the user is drifting
 * into a deficit they did not plan.
 *
 * Every judgement is made against the phase's own stored range, never against
 * this file (D-065).
 */
export function evaluateMaintenance(facts: DerivedFacts): Candidate {
  const phase = facts.phase
  if (!phase || phase.rangeSide === undefined) {
    throw new Error('maintenance rules require a phase and a comparable week')
  }

  /* `rangeSide`, not `ratePosition`. A maintenance range straddles zero, so
     "faster than the range" has no meaning — and the direction heuristic that
     serves the other four phases reads a range with a negative lower bound as a
     losing phase, which classified a 0.6% GAIN as "slower than range". Caught by
     the phase matrix. */
  if (phase.rangeSide === 'inside') return stableInsideRange(facts)
  if (phase.rangeSide === 'below') return driftingBelowRange(facts)

  /* Above the range. Whether that calls for a correction turns on the WAIST,
     not on how long the phase has been running.
     ─────────────────────────────────────────────────────────────────────────
     No new threshold was invented for this. "How many weeks after a cut counts
     as brief" would have been a coaching number nobody approved; the evidence
     hierarchy (D-005) already answers the same question with values that are
     approved. Weight up with the waist not following is the documented
     "glycogen, water and food volume" case and the documented "do not
     reflexively reduce intake". Weight and waist up together is both signals
     agreeing, which is what justifies one small correction. */
  return facts.waist.direction === 'rising' ? sustainedAboveRange(facts) : briefPostCutRise(facts)
}

function stableInsideRange(facts: DerivedFacts): Candidate {
  return {
    category: 'stayTheCourse',
    primaryAction:
      'Keep everything as it is. Holding steady inside your range is exactly what this phase is for — this is the phase working, not a plateau.',
    supportingActions: [],
    ruleId: RULE_IDS.maintenanceStable,
    family: 'phaseProgress',
    leadingEvidence: 'weight',
    evidence: evidenceFor(facts),
    watchItems: watchItemsFor(facts),
    missingEvidence: missingEvidenceFor(facts),
    rejectedAlternatives: [
      {
        action: 'Reduce intake because the scale is not falling',
        reason:
          'A stable weight is the objective of a maintenance phase. There is nothing here for a reduction to fix.',
      },
    ],
    reducesIntakeOrActivity: false,
  }
}

/**
 * Weight is up but the waist is not following.
 *
 * The documented "brief post-cut gain" case, and also the documented "weight up,
 * waist down: do not reflexively reduce intake". Reacting to the scale alone
 * would be acting on the weaker signal (D-005).
 *
 * The explanation is offered as **possible, never proven** (D-040): the app
 * says what usually accounts for this, not what did.
 */
function briefPostCutRise(facts: DerivedFacts): Candidate {
  return {
    category: 'doNotAdjustYet',
    primaryAction:
      'Change nothing yet. A rise in the first weeks after a deficit is often glycogen, food volume and water coming back rather than fat gained.',
    supportingActions: [],
    ruleId: RULE_IDS.maintenancePostCutRise,
    family: 'phaseProgress',
    leadingEvidence: 'weight',
    evidence: evidenceFor(facts),
    watchItems: [
      'If the waist rises with the weight over the next two or three weeks, that is the point to look at intake.',
      ...watchItemsFor(facts),
    ],
    missingEvidence: missingEvidenceFor(facts),
    rejectedAlternatives: [
      {
        action: 'Cut intake to bring the weight back down',
        reason:
          'Restoration after a deficit is expected and usually settles on its own. Cutting now would react to water rather than to fat.',
      },
    ],
    reducesIntakeOrActivity: false,
  }
}

/**
 * Sustained above the range, with the waist agreeing.
 *
 * `reducesIntakeOrActivity` is **true**, so D-007's hard filter refuses this
 * whenever adherence cannot be judged. That is the intended behaviour: this is
 * the one maintenance rule that proposes eating less.
 */
function sustainedAboveRange(facts: DerivedFacts): Candidate {
  return {
    category: 'considerOneSmallAdjustment',
    primaryAction:
      'Make one small reduction to bring the weight back into your maintenance range. Both the scale and your waist have moved up together.',
    supportingActions: [],
    ruleId: RULE_IDS.maintenanceSustainedAbove,
    family: 'phaseProgress',
    leadingEvidence: 'waist',
    evidence: evidenceFor(facts),
    watchItems: watchItemsFor(facts),
    missingEvidence: missingEvidenceFor(facts),
    rejectedAlternatives: [
      {
        action: 'Start a fat-loss phase',
        reason:
          'One week above your range is a reason for one small correction, not for changing the phase you are in.',
      },
    ],
    reducesIntakeOrActivity: true,
  }
}

/**
 * Drifting below the range.
 *
 * Losing weight during maintenance is off target, and the correction is to eat
 * *more* — which is why this never trips the D-007 filter.
 */
function driftingBelowRange(facts: DerivedFacts): Candidate {
  return {
    category: 'considerOneSmallAdjustment',
    primaryAction:
      'Eat a little more. You are losing weight during a phase meant to hold it steady, which usually means intake has drifted down without being planned.',
    supportingActions: [],
    ruleId: RULE_IDS.maintenanceBelowRange,
    family: 'phaseProgress',
    leadingEvidence: 'weight',
    evidence: evidenceFor(facts),
    watchItems: [
      'Unplanned loss during maintenance is worth watching: it is where appetite and training quality usually suffer next.',
      ...watchItemsFor(facts),
    ],
    missingEvidence: missingEvidenceFor(facts),
    rejectedAlternatives: [
      {
        action: 'Treat the loss as progress',
        reason:
          'This phase is for holding weight steady. Loss here is off target, however welcome it looks on the scale.',
      },
    ],
    reducesIntakeOrActivity: false,
  }
}
